
import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CircularProgress } from '@/components/ui/circular-progress';
import { Sparkles } from 'lucide-react';
import { directWellnessService } from '@/services/wellness/directWellnessService';
import { WellnessAnalysis } from '@/services/wellness';
import { useCurrentUserFamily } from '@/hooks/useCurrentUserFamily';
import { useAnalytics } from '@/hooks/useAnalytics';
import { format } from 'date-fns';

interface WeeklyWellnessScoresProps {
  onDatesAvailable?: (startDate: Date, endDate: Date) => void;
}

const WeeklyWellnessScores: React.FC<WeeklyWellnessScoresProps> = ({ onDatesAvailable }) => {
  const { familyId } = useCurrentUserFamily();
  const { track } = useAnalytics();

  const { 
    data: wellnessAssessment, 
    isLoading, 
    error
  } = useQuery({
    queryKey: ['latest-wellness-assessment', familyId],
    queryFn: () => directWellnessService.getLatestWellnessAssessment(familyId!),
    enabled: !!familyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1
  });

  const analysis: WellnessAnalysis | null = wellnessAssessment?.wellness_analysis || null;
  const weekStartDate = wellnessAssessment?.week_start_date;
  const weekEndDate = wellnessAssessment?.week_end_date;

  // Notify parent component when dates are available
  useEffect(() => {
    if (weekStartDate && weekEndDate && onDatesAvailable) {
      onDatesAvailable(new Date(weekStartDate), new Date(weekEndDate));
    }
  }, [weekStartDate, weekEndDate, onDatesAvailable]);

  // Track when wellness scores are viewed
  useEffect(() => {
    if (analysis && analysis.scores && weekStartDate) {
      track('wellness_score_viewed', {
        week_start_date: weekStartDate,
        avg_score: Object.values(analysis.scores).reduce((a, b) => a + b, 0) / Object.values(analysis.scores).length,
        score_count: Object.keys(analysis.scores).length,
      });
    }
  }, [analysis, track, weekStartDate]);



  // Define score types for different wellness metrics
  const getScoreType = (key: string): 'higher-better' | 'lower-better' | 'moderate-better' => {
    if (key === 'processed_food_frequency' || key === 'saturated_fat_moderation') {
      return 'lower-better';
    } else if (key === 'carb_intake') {
      return 'moderate-better';
    } else {
      return 'higher-better'; // fiber_intake, vegetable_diversity, plant_to_animal_balance
    }
  };

  const formatScoreLabel = (key: string): string => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };


  if (isLoading) {
    return (
      <Card className="mb-6">
        <CardContent className="space-y-4 pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mb-6 border-red-200">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            Error loading wellness assessment. Please try again later.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!wellnessAssessment || !analysis || !analysis.scores) {
    return (
      <Card className="mb-6 border-orange-200">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            No wellness assessment available yet. Assessments are generated automatically when meal data is available.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
      <CardContent className="space-y-6 pt-6">
        {/* Nutrition Scores */}
        <div className="space-y-4">
          <h3 className="font-medium text-gray-900 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-blue-600" />
            How you did last week
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {['fiber_intake', 'vegetable_diversity', 'plant_to_animal_balance', 'carb_intake', 'saturated_fat_moderation', 'processed_food_frequency'].map((key) => {
              const score = analysis.scores[key as keyof typeof analysis.scores];
              const justification = analysis.justifications?.[key as keyof typeof analysis.justifications];
              const tip = analysis.tips?.[key as keyof typeof analysis.tips];
              const scoreType = getScoreType(key);
              
              return (
                <div key={key} className="bg-white rounded-lg p-6 border shadow-sm min-h-[160px]">
                  {/* Title positioned at top */}
                  <h4 className="font-semibold text-base text-gray-900 mb-4 text-center sm:text-left">
                    {formatScoreLabel(key)}
                  </h4>
                  
                  {/* Responsive content container */}
                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
                    {/* Fixed circle container for alignment */}
                    <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center">
                      <CircularProgress 
                        value={score * 20} // Convert 1-5 score to 0-100 percentage
                        rawScore={score} // Pass raw score for color coding
                        size={80}
                        strokeWidth={6}
                        scoreType={scoreType}
                      />
                    </div>
                    
                    {/* Text container */}
                    <div className="flex-1 space-y-2 text-center sm:text-left">
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {justification || 'No detailed explanation available for this score.'}
                      </p>
                      {tip && (
                        <p className="text-sm text-blue-700 font-medium leading-relaxed">
                          {tip}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        
        {/* Legal Disclaimer */}
        <div className="border-t border-gray-200 pt-4 mt-6">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            This wellness assessment is generated using AI technology and is not intended as professional medical, nutritional, or health advice. 
            Please use your own judgment and consult with qualified professionals for personalized guidance.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default WeeklyWellnessScores;
